import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import {
  api, getInventoryDivergences, getInventoryPasses, getInventoryRun,
  getMovementRate, getStructureAisleMetrics, getStructureOccupancy,
  getStructureSlotting, latestPaintableRun, listInventoryRuns,
} from "../../api/rest";
import type { DivergenceSummary } from "../../api/rest";
import { ageBuckets, expiryBuckets } from "../../pages/inventoryAging";
import { getDisplayStatus, useTelemetryStore } from "../../stores/telemetryStore";
import { AGREEMENT_WARN_PCT, leadAisle, needsAttention } from "./aisleAttention";
import { SegmentBar, WidgetCard } from "./WidgetCard";
import type { WidgetDef, WidgetProps } from "./widget";
import css from "./Home.module.css";

const pct = (v: number | null | undefined, lang: string) =>
  v == null ? null : `${v.toLocaleString(lang, { maximumFractionDigits: 1 })}`;

/** Counts carry a thousands separator on the UI language — pt-BR groups with a
 *  dot, so a bare String(13035) is not merely unformatted, it is unreadable. */
const int = (v: number | null | undefined, lang: string) =>
  v == null ? null : v.toLocaleString(lang);

const todayStart = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

/** ADR 314: the warehouse's m² rate, or null while unconfigured — cost lines only
 *  render on a configured rate, never on a guess. Query key shared with the
 *  Tarifas editor, so a save there refreshes these cards. */
function useSpaceCost(siteId: string, warehouseId?: string) {
  const { data: rate } = useQuery({
    queryKey: ["movementRate", siteId, warehouseId],
    queryFn: () => getMovementRate(siteId, warehouseId!),
    enabled: !!siteId && !!warehouseId,
    staleTime: 60_000,
  });
  return rate?.sqmMonthCost != null ? { perSqm: rate.sqmMonthCost, currency: rate.currency } : null;
}

/** Whole-currency monthly amounts; a free-text currency code degrades to a plain
 *  number with the code appended rather than throwing the widget away. */
const money = (v: number, currency: string, lang: string) => {
  try {
    return new Intl.NumberFormat(lang, { style: "currency", currency, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${Math.round(v).toLocaleString(lang)} ${currency}`;
  }
};

/** The newest non-failed run for the active warehouse — the one every inventory
 *  widget reports on, resolved once per widget through a shared query key so the
 *  list is fetched once for the whole page. */
function useLatestRun(siteId: string, warehouseId?: string) {
  const { data: runs, isLoading } = useQuery({
    queryKey: ["inventory-runs", siteId],
    queryFn: () => listInventoryRuns(siteId),
    enabled: !!siteId,
    staleTime: 60_000,
  });
  return { run: warehouseId ? latestPaintableRun(runs ?? [], warehouseId) : undefined, isLoading };
}

// ── Fleet ────────────────────────────────────────────────────────────────────

function FleetOnline() {
  const { t, i18n } = useTranslation();
  // Live from the telemetry socket, not a poll: this is the one number on the
  // page that is already streaming, and re-fetching it would be slower AND
  // disagree with /fleet.
  const forklifts = useTelemetryStore((s) => s.forklifts);
  const machines = Object.values(forklifts);
  const online = machines.filter((f) => getDisplayStatus(f) !== "offline").length;

  return (
    <WidgetCard
      title={t("home.fleet.title")}
      value={machines.length ? int(online, i18n.language) : null}
      hint={machines.length ? t("home.fleet.of", { total: int(machines.length, i18n.language) }) : t("home.fleet.none")}
      tone={machines.length && online === 0 ? "warn" : "neutral"}
      to="/fleet"
    />
  );
}

// ── OEE ──────────────────────────────────────────────────────────────────────

function OeeToday({ siteId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["oee-modular-today", siteId],
    queryFn: () => api.getOeeModular(siteId, todayStart(), new Date()),
    refetchInterval: (q) => (q.state.status === "error" ? 60_000 : 60_000),
    retry: 1,
    enabled: !!siteId,
  });

  // Capability grading (ADR 107-109): a proxy-tier product is "OEE estimado",
  // and with a factor unmeasured there is no OEE at all — only Indicadores.
  // Reporting a bare percentage in that state would invent a measurement.
  // Both are fractions on the wire, and both are OPTIONAL: `oee` is absent
  // unless all three factors are present, `composite` unless the opt-in is on.
  // Multiplying an undefined by 100 is how a missing measurement becomes "NaN%".
  const grade = data?.grade;
  const fraction = grade === "INDICADORES" ? data?.composite : data?.oee;
  const value = fraction == null ? null : pct(fraction * 100, i18n.language);

  return (
    <WidgetCard
      title={grade === "OEE_ESTIMADO" ? t("kpi.oee.estimated")
           : grade === "INDICADORES" ? t("kpi.oee.indicators")
           : t("kpi.oee.today")}
      value={value}
      unit="%"
      loading={isLoading}
      hint={grade === "INDICADORES" && data?.compositeLabel ? data.compositeLabel : undefined}
      to="/oee"
    />
  );
}

// ── Inventory: coverage of the last count ────────────────────────────────────

function LastCount({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-run", siteId, run?.id],
    queryFn: () => getInventoryRun(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });

  if (!runsLoading && !run) {
    return <WidgetCard title={t("home.lastCount.title")} value={null}
                       hint={t("home.lastCount.none")} to="/inventory" />;
  }

  // ADR 410: the percentage is the number; "read of total" sits under it and is
  // the only other thing on the card. A run with no locations master has no total
  // (ADR 122) and shows the read count alone.
  const visited = data?.kpis.addressesScanned;
  const total = data?.kpis.addressTotal;
  const coverage = data?.kpis.coveragePct;
  const hasTotal = visited != null && total != null && coverage != null;
  return (
    <WidgetCard
      title={t("home.lastCount.title")}
      value={visited == null ? null
        : hasTotal ? pct(coverage, i18n.language)
        : int(visited, i18n.language)}
      unit={hasTotal ? "%" : undefined}
      hint={hasTotal ? t("home.lastCount.value", { visited: int(visited, i18n.language), total: int(total, i18n.language) }) : undefined}
      loading={runsLoading || isLoading}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    />
  );
}

// ── Inventory: agreement with the WMS ────────────────────────────────────────

/** ADR 410: the backend always answers with a summary; `asOf` is null when no WMS
 *  snapshot was ever imported for the run's warehouse. That — not a missing
 *  summary — is "no WMS", and the three WMS cards render nothing without it. */
function hasWms(s: DivergenceSummary | null | undefined): s is DivergenceSummary {
  return !!s && s.asOf != null;
}

function WmsAccuracy({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-divergences", siteId, run?.id],
    queryFn: () => getInventoryDivergences(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });

  // ADR 410: the third slice — stock the kit saw but could not read. Same query
  // key as the Estoque não identificado card, so it costs nothing extra.
  const { data: passes } = useQuery({
    queryKey: ["inventory-passes", siteId, run?.id],
    queryFn: () => getInventoryPasses(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });

  const s = hasWms(data?.summary) ? data!.summary : undefined;
  // ADR 410: no WMS, no card. "Sem posição do WMS para comparar" was a tile that
  // said nothing on every demo without expected stock.
  if (!runsLoading && !isLoading && !s) return null;
  const nok = s ? s.qtyMismatch + s.missingInIc3 + s.unexpectedInIc3 : 0;
  const noLabel = passes?.unidentifiedStock.length ?? 0;
  return (
    <WidgetCard
      title={t("home.accuracy.title")}
      value={s ? pct(s.agreementPct, i18n.language) : null}
      unit="%"
      loading={runsLoading || isLoading}
      tone={!s ? "neutral" : s.agreementPct >= 95 ? "good" : s.agreementPct >= 80 ? "warn" : "bad"}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    >
      {s && (
        <SegmentBar
          ariaLabel={t("home.accuracy.barAria")}
          segments={[
            { key: "ok", label: t("home.accuracy.okWms"), value: s.matched, token: "var(--color-status-loaded)" },
            { key: "nok", label: t("home.accuracy.nokWms"), value: nok, token: "var(--color-alert-crit)" },
            { key: "nolabel", label: t("home.accuracy.noLabel"), value: noLabel, token: "var(--color-status-idle)" },
          ]}
        />
      )}
    </WidgetCard>
  );
}

// ── Inventory: how full the warehouse is ─────────────────────────────────────

function Occupancy({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    // depth 0 — roots only (ADR 276). This card sums the roots and never looks
    // deeper, but asked for the whole graph until now: 6 MB of individual spaces
    // at JSL to render one percentage. The numbers are byte-identical.
    queryKey: ["structure-occupancy", siteId, warehouseId, run?.id, 0],
    queryFn: () => getStructureOccupancy(siteId, warehouseId!, run!.id, 0),
    enabled: !!warehouseId && !!run,
    staleTime: 60_000,
  });

  // Sum the roots: the rollup is already subtree-summed, so the roots together
  // are the warehouse.
  const roots = data?.nodes ?? [];
  const sum = (f: (o: (typeof roots)[number]["occupancy"]) => number) =>
    roots.reduce((a, n) => a + f(n.occupancy), 0);
  const occupiable = sum((o) => o.occupiableSpaces);
  const occupied = sum((o) => o.occupiedSpaces);
  // ADR 292: the slots say how many are used, not how much of each. The vertical mean is
  // re-derived from the roots' own means, weighted by the bins each measured.
  const measured = sum((o) => o.verticalMeasuredSpaces);
  const verticalFill = measured > 0
    ? sum((o) => (o.verticalFillPct ?? 0) * o.verticalMeasuredSpaces) / measured
    : null;
  // ADR 293: any bin divided by a clear height the twin states (not one the kit
  // measured) makes the whole mean an estimate, and the hint says which it is.
  const verticalDerived = sum((o) => o.verticalDerivedSpaces) > 0;

  // ADR 314: what the confirmed-occupied floor costs per month. Same slotting
  // query the Posições livres card runs — the cache serves both.
  const cost = useSpaceCost(siteId, warehouseId);
  const { data: slotting } = useQuery({
    queryKey: ["structure-slotting", siteId, warehouseId, run?.id],
    queryFn: () => getStructureSlotting(siteId, warehouseId!, run!.id),
    enabled: !!warehouseId && !!run && cost != null,
    staleTime: 60_000,
  });
  const occCost = cost && slotting?.summary?.occupiedAreaSqm != null
    ? slotting.summary.occupiedAreaSqm * cost.perSqm : null;
  const hintParts = [
    verticalFill == null ? null
      : t(verticalDerived ? "home.occupancy.verticalDerived" : "home.occupancy.vertical", {
          pct: pct(verticalFill, i18n.language),
          count: measured, n: int(measured, i18n.language),
        }),
    occCost != null && cost
      ? <strong className="cost">{t("home.occupancy.cost", { amount: money(occCost, cost.currency, i18n.language) })}</strong> : null,
  ].filter(Boolean);

  return (
    <WidgetCard
      title={t("home.occupancy.title")}
      value={occupiable > 0 ? pct((occupied / occupiable) * 100, i18n.language) : null}
      unit="%"
      loading={runsLoading || isLoading}
      to="/live"
      hint={hintParts.length ? hintParts.map((p, i) => <Fragment key={i}>{i > 0 && " · "}{p}</Fragment>) : undefined}
    >
      {occupiable > 0 && (
        <SegmentBar
          ariaLabel={t("home.occupancy.barAria")}
          segments={[
            { key: "occupied", label: t("inventory.item.counted"), value: occupied,
              token: "var(--color-status-loaded)" },
            { key: "empty", label: t("inventory.item.empty"), value: sum((o) => o.emptySpaces),
              token: "var(--color-status-empty)" },
            // not-scanned is NOT empty: an aisle the run never walked would read
            // as free space, which is the opposite of what it means.
            { key: "notScanned", label: t("inventory.item.not_scanned"),
              value: sum((o) => o.notScannedSpaces), token: "var(--op-unvisited)" },
          ]}
        />
      )}
    </WidgetCard>
  );
}

// ── Inventory: how old the stock is ──────────────────────────────────────────

function Aging({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  // Same query key as the accuracy widget, so this costs no extra request.
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-divergences", siteId, run?.id],
    queryFn: () => getInventoryDivergences(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });

  // Age is the WMS FIFO date against the snapshot's asOf, never a scan
  // timestamp (ADR 194) — a scan says when a camera saw a bin, not how long
  // the pallet has been in the building.
  const rows = data?.rows ?? [];
  const buckets = ageBuckets(rows);
  const lines = (b: (typeof buckets)[number]) => b.match + b.qtyMismatch + b.missing;
  const total = buckets.reduce((a, b) => a + lines(b), 0);
  // "Older than 90 days" = everything past the d90 bucket.
  const old = buckets.slice(2).reduce((a, b) => a + lines(b), 0);

  // ADR 410: no WMS, no card.
  if (!runsLoading && !isLoading && !hasWms(data?.summary)) return null;
  return (
    <WidgetCard
      title={t("home.aging.title")}
      // No FIFO dates in the snapshot means no ages — not an age of zero.
      value={total > 0 ? pct((old / total) * 100, i18n.language) : null}
      unit="%"
      loading={runsLoading || isLoading}
      tone={total === 0 ? "neutral" : old / total >= 0.25 ? "warn" : "good"}
      hint={total > 0 ? t("home.aging.hint", { items: int(old, i18n.language) }) : t("home.aging.noDates")}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    >
      {total > 0 && (
        <SegmentBar
          ariaLabel={t("home.aging.barAria")}
          segments={[
            { key: "d30", label: t("inventory.aging.d30"), value: lines(buckets[0]),
              token: "var(--color-heat-0)" },
            { key: "d90", label: t("inventory.aging.d90"), value: lines(buckets[1]),
              token: "var(--color-heat-1)" },
            { key: "d180", label: t("inventory.aging.d180"), value: lines(buckets[2]),
              token: "var(--color-heat-2)" },
            { key: "d365", label: t("inventory.aging.d365"), value: lines(buckets[3]),
              token: "var(--color-heat-3)" },
            { key: "over", label: t("inventory.aging.over"), value: lines(buckets[4]),
              token: "var(--color-heat-4)" },
          ]}
        />
      )}
    </WidgetCard>
  );
}

// ── Inventory: shelf life (FEFO) ──────────────────────────────────────────────

/** ADR 410: the FEFO twin of the FIFO card. Some sites rotate on the receiving
 *  date (FIFO), some on the expiry date (FEFO), and the two disagree — a pallet
 *  received last can expire first. Hidden when the snapshot carries no expiry. */
function Fefo({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-divergences", siteId, run?.id],
    queryFn: () => getInventoryDivergences(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });
  const rows = data?.rows ?? [];
  const buckets = expiryBuckets(rows);
  const total = buckets.reduce((a, b) => a + b.items, 0);
  // "At risk" = expired plus everything that expires within 90 days.
  const soon = buckets.slice(0, 3).reduce((a, b) => a + b.items, 0);
  // ADR 410: no WMS, no card.
  if (!runsLoading && !isLoading && !hasWms(data?.summary)) return null;
  return (
    <WidgetCard
      title={t("home.fefo.title")}
      value={total > 0 ? pct((soon / total) * 100, i18n.language) : null}
      unit="%"
      loading={runsLoading || isLoading}
      tone={total === 0 ? "neutral" : soon / total >= 0.25 ? "warn" : "good"}
      hint={total > 0 ? t("home.fefo.hint", { items: int(soon, i18n.language) }) : t("home.fefo.noDates")}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    >
      {total > 0 && (
        <SegmentBar
          ariaLabel={t("home.fefo.barAria")}
          segments={buckets.map((b, i) => ({
            key: b.key, label: t(`home.fefo.bucket.${b.key}`), value: b.items,
            token: `var(--color-heat-${4 - i})`,
          }))}
        />
      )}
    </WidgetCard>
  );
}

// ── Inventory: stock the kit could not identify ──────────────────────────────

function UnidentifiedStock({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["inventory-passes", siteId, run?.id],
    queryFn: () => getInventoryPasses(siteId, run!.id),
    enabled: !!run,
    staleTime: 60_000,
  });

  // Bins the sensor measured as occupied with no readable label (ADR 263):
  // stock is present, identity is not. Every one is a walk-out-and-look.
  const n = data?.unidentifiedStock.length;
  return (
    <WidgetCard
      title={t("home.unidentified.title")}
      value={int(n, i18n.language)}
      loading={runsLoading || isLoading}
      tone={n ? "warn" : "good"}
      hint={t("home.unidentified.hint")}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    />
  );
}

// ── Inventory: what to move, and where it fits ───────────────────────────────

function Slotting({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["structure-slotting", siteId, warehouseId, run?.id],
    queryFn: () => getStructureSlotting(siteId, warehouseId!, run!.id),
    enabled: !!warehouseId && !!run,
    staleTime: 60_000,
  });

  // Lead with free capacity: it is the number a supervisor acts on before a
  // shift, where stray loads are a cleanup list.
  const s = data?.summary;
  const cost = useSpaceCost(siteId, warehouseId);
  // ADR 314: % of the positions the run CONFIRMED (occupied + empty) — not of all
  // positions, where the unscanned would read as anything the reader hopes.
  const confirmed = s?.occupiedStorableSpaces != null
    ? s.emptyStorableSpaces + s.occupiedStorableSpaces : 0;
  const emptyCost = cost && s?.emptyAreaSqm != null ? s.emptyAreaSqm * cost.perSqm : null;
  const hintParts = [
    emptyCost != null && cost
      ? <strong className="cost">{t("home.slotting.cost", { amount: money(emptyCost, cost.currency, i18n.language) })}</strong> : null,
    s ? t("home.slotting.hint", { stray: int(s.strayLoads, i18n.language),
                                  consolidate: int(s.consolidationCandidates, i18n.language) }) : null,
  ].filter(Boolean);
  return (
    <WidgetCard
      title={t("home.slotting.title")}
      value={int(s?.emptyStorableSpaces, i18n.language)}
      unit={s && confirmed > 0
        ? `· ${pct((s.emptyStorableSpaces / confirmed) * 100, i18n.language)} %` : undefined}
      loading={runsLoading || isLoading}
      hint={hintParts.length ? hintParts.map((p, i) => <Fragment key={i}>{i > 0 && " · "}{p}</Fragment>) : undefined}
      to={run ? `/inventory/runs/${run.id}` : "/inventory"}
    />
  );
}

// ── Inventory: one aisle at a time ───────────────────────────────────────────

/**
 * ADR 276. Occupancy, WMS agreement and FIFO age for a single aisle, opening on the one
 * that most needs a supervisor and letting them walk the rest.
 *
 * Three things this card deliberately does not say:
 * - **no dwell time.** Time-in-slot needs the same pallet seen twice; there is one run per
 *   warehouse and `InventoryMovement` carries no timestamps. The age here is the WMS FIFO
 *   date — time since the pallet entered the *building* (ADR 194/203/261).
 * - **no "average occupancy".** It would need a time axis, and one run is not one.
 * - **no zeros for absent data.** An aisle with no comparison shows an em dash, not 0 %.
 */
function AisleFocus({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { run, isLoading: runsLoading } = useLatestRun(siteId, warehouseId);
  const { data, isLoading } = useQuery({
    queryKey: ["structure-aisle-metrics", siteId, warehouseId, run?.id],
    queryFn: () => getStructureAisleMetrics(siteId, warehouseId!, run!.id),
    enabled: !!warehouseId && !!run,
    staleTime: 60_000,
  });

  const aisles = data?.aisles ?? [];
  const [picked, setPicked] = useState<string | null>(null);
  // The pick is by id, resolved each render: a run refresh that drops an aisle must fall
  // back to the lead rather than blank the card holding a dead id.
  const lead = leadAisle(aisles);
  const sel = aisles.find((a) => a.id === picked) ?? lead;

  if (!runsLoading && !isLoading && aisles.length === 0) {
    return (
      <WidgetCard title={t("home.aisles.title")} value={null}
                  hint={run ? t("home.aisles.noAisles") : t("home.aisles.noRun")}
                  to={run ? "/live" : "/inventory"} />
    );
  }

  const occ = sel?.occupancy;
  const div = sel?.divergence;
  const aging = sel?.aging;

  return (
    <WidgetCard
      title={t("home.aisles.title")}
      caption={sel ? t("home.aisles.aisle", { code: sel.code ?? "—" }) : undefined}
      value={occ ? pct(occ.occupancyPct, i18n.language) : null}
      unit="%"
      loading={runsLoading || isLoading}
      // Neutral, deliberately. The big number is OCCUPANCY, and tinting it by the
      // agreement would put a warning colour on a fullness figure — a reader sees
      // "100 %" in orange and concludes the aisle being full is the problem. The
      // agreement carries its own emphasis, on itself, below.
      hint={
        <Link to="/live" className={css.aisleLink}>{t("home.aisles.open")}</Link>
      }
    >
      {occ && (
        <SegmentBar
          ariaLabel={t("home.occupancy.barAria")}
          segments={[
            { key: "occupied", label: t("inventory.item.counted"), value: occ.occupiedSpaces,
              token: "var(--color-status-loaded)" },
            { key: "empty", label: t("inventory.item.empty"), value: occ.emptySpaces,
              token: "var(--color-status-empty)" },
            { key: "notScanned", label: t("inventory.item.not_scanned"), value: occ.notScannedSpaces,
              token: "var(--op-unvisited)" },
          ]}
        />
      )}

      {/* ADR 410: the three WMS figures only when the aisle has a WMS behind it —
          a row of dashes on a kit-only demo says nothing. */}
      {sel && (div || aging) && (
        <div className={css.aisleStats}>
          <div className={css.aisleStat}>
            {t("home.accuracy.title")}
            <b className={div && div.agreementPct < AGREEMENT_WARN_PCT ? css.warn : undefined}>
              {div ? `${pct(div.agreementPct, i18n.language)} %` : "—"}
            </b>
          </div>
          <div className={css.aisleStat}>
            {t("home.aisles.avgAge")}
            <b>{aging ? t("home.aisles.days", { days: int(Math.round(aging.avgAgeDays), i18n.language) }) : "—"}</b>
          </div>
          <div className={css.aisleStat}>
            {t("home.aisles.over90")}
            <b>{aging ? int(aging.over90Days, i18n.language) : "—"}</b>
          </div>
        </div>
      )}

      {aisles.length > 1 && (
        <div className={css.aisleChips} role="group" aria-label={t("home.aisles.pickerAria")}>
          {aisles.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`${css.aisleChip} ${a.id === sel?.id ? css.aisleChipOn : ""}`}
              aria-pressed={a.id === sel?.id}
              onClick={() => setPicked(a.id)}
            >
              {a.code ?? "?"}
              {needsAttention(a) && (
                <span className={css.aisleChipMark}
                      role="img"
                      aria-label={t("home.aisles.attentionAria")} />
              )}
            </button>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}

// ── Alerts: what needs a person now ──────────────────────────────────────────

/** ADR 282: the widget ADR 274 kept out until alerting existed. Counts OPEN
 *  alerts (impact + inventory rules); a zero is now a measured all-clear, not a
 *  manufactured one. No capability gate — the rules span verticals. */
function OpenAlerts({ siteId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["alerts-open", siteId],
    queryFn: () => api.listAlerts(siteId, "open"),
    enabled: !!siteId,
    refetchInterval: 30_000,
  });
  const crit = (data ?? []).filter((a) => a.severity === "CRIT").length;
  return (
    <WidgetCard
      title={t("home.alerts.title")}
      value={data ? int(data.length, i18n.language) : null}
      loading={isLoading}
      tone={crit > 0 ? "bad" : data && data.length > 0 ? "warn" : "good"}
      hint={crit > 0 ? t("home.alerts.crit", { count: crit }) : t("home.alerts.hint")}
      to="/alerts"
    />
  );
}

// ── Telemetry: driving safety ────────────────────────────────────────────────

function SafetyImpacts({ siteId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  // A week, not a day: impacts are rare enough that a 24 h window reads zero
  // almost always, which teaches the reader to ignore the card.
  const [from] = useState(() => Date.now() - 7 * 24 * 3600_000);
  const [to] = useState(() => Date.now());
  const { data, isLoading } = useQuery({
    queryKey: ["safety-impacts-7d", siteId, from],
    queryFn: () => api.impacts(siteId, from, to),
    enabled: !!siteId,
    staleTime: 300_000,
  });

  const severe = (data ?? []).filter((i) => i.severity === "SEVERO").length;
  return (
    <WidgetCard
      title={t("home.safety.title")}
      value={data ? int(data.length, i18n.language) : null}
      loading={isLoading}
      tone={severe > 0 ? "bad" : data && data.length > 0 ? "warn" : "good"}
      hint={severe > 0 ? t("home.safety.severe", { count: severe }) : t("home.safety.window")}
      to="/safety"
    />
  );
}

// ── RTLS: link stability ─────────────────────────────────────────────────────

function NetworkDisruptions({ siteId, warehouseId }: WidgetProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["network-disruptions-24h", siteId, warehouseId],
    queryFn: () => api.getDisruptions(siteId, 24, 50, warehouseId),
    enabled: !!siteId,
    staleTime: 120_000,
  });

  return (
    <WidgetCard
      title={t("home.network.title")}
      value={data ? int(data.length, i18n.language) : null}
      loading={isLoading}
      tone={data && data.length > 0 ? "warn" : "good"}
      hint={t("home.network.window")}
      to="/network"
    />
  );
}

/**
 * Today's arrangement (ADR 273 stage 0/1). A fixed, ordered list of ids — no
 * persistence, no picker. Each entry declares its own gate, so this list is the
 * only thing stage 2 has to replace.
 *
 * Order is deliberate: what the fleet is doing, then what the last count found.
 *
 * The open-alerts widget leads: it is the one number that means "someone should
 * act now", and it exists since ADR 282 (impact + inventory rules write the
 * `alert` table — the pre-282 objection was that the count was manufactured).
 */
export const HOME_WIDGETS: WidgetDef[] = [
  { id: "open-alerts", titleKey: "home.alerts.title", to: "/alerts",
    requiresProvisioning: "site",
    Component: OpenAlerts },
  { id: "fleet-online", titleKey: "home.fleet.title", to: "/fleet",
    requiresCapability: "rtls", requiresProvisioning: "site",
    Component: FleetOnline },
  { id: "network-disruptions", titleKey: "home.network.title", to: "/network",
    requiresCapability: "rtls", requiresProvisioning: "site",
    Component: NetworkDisruptions },
  { id: "oee-today", titleKey: "kpi.oee.today", to: "/oee",
    requiresCapability: "telemetry", requiresProvisioning: "site",
    Component: OeeToday },
  { id: "safety-impacts", titleKey: "home.safety.title", to: "/safety",
    requiresCapability: "telemetry", requiresProvisioning: "site",
    Component: SafetyImpacts },
  { id: "last-count", titleKey: "home.lastCount.title", to: "/inventory",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: LastCount },
  { id: "wms-accuracy", titleKey: "home.accuracy.title", to: "/inventory",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: WmsAccuracy },
  { id: "unidentified", titleKey: "home.unidentified.title", to: "/inventory",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: UnidentifiedStock },
  { id: "slotting", titleKey: "home.slotting.title", to: "/inventory",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: Slotting },
  { id: "occupancy", titleKey: "home.occupancy.title", to: "/live", size: "wide",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: Occupancy },
  { id: "aisle-focus", titleKey: "home.aisles.title", to: "/live", size: "wide",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: AisleFocus },
  { id: "aging", titleKey: "home.aging.title", to: "/inventory", size: "wide",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: Aging },
  // ADR 410: shelf-life beside age — FIFO and FEFO sites read different cards.
  { id: "fefo", titleKey: "home.fefo.title", to: "/inventory", size: "wide",
    requiresCapability: "inventory", requiresProvisioning: "site", salesAllowed: true,
    Component: Fefo },
];
